import React, { useState } from "react";
import FaiBox from "../../../../../MuiComponents/FaiBox";
import {
	Card,
	Grid,
	Step,
	StepLabel,
	Stepper,
	CircularProgress,
} from "@mui/material";
import FaiButton from "../../../../../MuiComponents/FaiButton";
import DetailForm from "./component/DetailForm";
import VariantForm from "./component/VariantForm";
import { ProductController } from "../../../../../controllers/productController";
import { Product } from "../../../../../types/productTypes";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

function ProductForm() {
	const [activeStep, setActiveStep] = useState(0);
	const steps = ["Product Details", "Upload Images"];
	const [createdVariantId, setCreatedVariantId] = useState<string | undefined>(
		undefined
	);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const [successMessage, setSuccessMessage] = useState("");
	const navigate = useNavigate();
	const [formData, setFormData] = useState<Partial<Product>>({
		variants: [
			{
				externalProductId: "",
				externalProductIdType: "",
				relationshipType: "variant",
				skuCode: "",
				name: "",
				variantAttributes: [],
				pricing: {
					mrp: 0,
					sellingPrice: 0,
				},
				inventory: {
					quantity: 0,
				},
			},
		],
		shippingEssentials: {
			length: 0,
			breadth: 0,
			height: 0,
			weight: 0,
		},
		isReturnable: true,
	});

	const isLastStep = activeStep === steps.length - 1;

	const validateProductDetails = () => {
		const requiredFields = [
			"brand",
			"productName",
			"skuCode",
			"categoryId",
			"productTypeId",
			"manufacturer",
			"articleNumber",
			"description",
		];

		const missingFields = requiredFields.filter(
			(field) => !formData[field as keyof typeof formData]
		);

		if (missingFields.length > 0) {
			setErrors([
				`Please fill in all required fields: ${missingFields.join(", ")}`,
			]);
			return false;
		}

		setErrors([]);
		return true;
	};

	const handleNext = () => {
		if (activeStep === 0) {
			if (!validateProductDetails()) {
				return;
			}
		}
		setActiveStep(activeStep + 1);
	};

	const handleBack = () => setActiveStep(activeStep - 1);

	const handleSaveProduct = async (isDraft = false) => {
		// Validate form data
		if (!validateProductDetails()) {
			return;
		}

		setLoading(true);
		setErrors([]);
		setSuccessMessage("");

		try {
			// Create FormData for API call
			const productFormData = new FormData();

			// Add base product fields
			productFormData.append("skuCode", formData.skuCode || "");
			productFormData.append("brand", formData.brand || "");
			productFormData.append("productName", formData.productName || "");
			productFormData.append("manufacturer", formData.manufacturer || "");
			productFormData.append("articleNumber", formData.articleNumber || "");
			productFormData.append("description", formData.description || "");
			productFormData.append("categoryId", formData.categoryId || "");
			productFormData.append("productTypeId", formData.productTypeId || "");

			// Add metadata if any
			if (formData.metadata) {
				productFormData.append("metadata", JSON.stringify(formData.metadata));
			}

			// Add shipping essentials
			if (formData.shippingEssentials) {
				productFormData.append(
					"shippingEssentials.length",
					formData.shippingEssentials.length?.toString() || ""
				);
				productFormData.append(
					"shippingEssentials.breadth",
					formData.shippingEssentials.breadth?.toString() || ""
				);
				productFormData.append(
					"shippingEssentials.height",
					formData.shippingEssentials.height?.toString() || ""
				);
				productFormData.append(
					"shippingEssentials.weight",
					formData.shippingEssentials.weight?.toString() || ""
				);
			}

			// Add returnable status
			productFormData.append("isReturnable", "true");

			// Add sizeChartMedia file if present
			if (formData.sizeChartMedia) {
				const file = formData.sizeChartMedia;
				productFormData.append(`sizeChartMedia`, file, file.name);
			}

			// Add variant data
			if (formData.variants && formData.variants.length > 0) {
				formData.variants.forEach((variant, variantIndex) => {
					// Add variant base fields
					productFormData.append(
						`variants[${variantIndex}].externalProductId`,
						variant.externalProductId || `external-${Date.now()}`
					);
					productFormData.append(
						`variants[${variantIndex}].externalProductIdType`,
						variant.externalProductIdType || ""
					);
					productFormData.append(
						`variants[${variantIndex}].relationshipType`,
						"variant"
					);
					productFormData.append(
						`variants[${variantIndex}].skuCode`,
						variant.skuCode || ""
					);
					productFormData.append(
						`variants[${variantIndex}].name`,
						variant.name || ""
					);

					// Add variant attributes
					if (
						variant.variantAttributes &&
						variant.variantAttributes.length > 0
					) {
						variant.variantAttributes.forEach((attr, attrIndex) => {
							productFormData.append(
								`variants[${variantIndex}].variantAttributes[${attrIndex}].attribute`,
								attr.attribute
							);
							productFormData.append(
								`variants[${variantIndex}].variantAttributes[${attrIndex}].value`,
								attr.value
							);
						});
					}

					// Add pricing
					if (variant.pricing) {
						productFormData.append(
							`variants[${variantIndex}].price`,
							variant.pricing.sellingPrice.toString()
						);
					}

					// Add inventory
					if (variant.inventory) {
						productFormData.append(
							`variants[${variantIndex}].inventoryQuantity`,
							variant.inventory.quantity.toString()
						);
					}

					if (variant.media && variant.media.length > 0) {
						variant.media.forEach((mediaItem, mediaIndex) => {
							if (mediaItem instanceof File) {
								productFormData.append(
									`variants[${variantIndex}].media`,
									mediaItem,
									mediaItem.name
								);
							} else if (typeof mediaItem === "string") {
								productFormData.append(
									`variants[${variantIndex}].media`,
									mediaItem
								);
							}
						});
					}
				});
			}

			// Add draft status if needed
			if (isDraft) {
				productFormData.append("isDraft", "true");
			}

			// Submit the product
			const response = await ProductController.createProduct(productFormData);

			if (
				response.data &&
				response.data.data &&
				response.data.data.variants &&
				response.data.data.variants[0]
			) {
				setCreatedVariantId(response.data.data.variants[0].id);
				setSuccessMessage(
					isDraft
						? "Product saved as draft successfully!"
						: "Product created successfully!"
				);

				// Move to image upload step if not already there
				if (activeStep === 0) {
					setActiveStep(1);
				}
			}

			// Reached only when the API did NOT throw -> genuine success.
			toast.success(
				isDraft ? "Product saved as draft" : "Product created successfully"
			);
			navigate("/app/catalog/draft");
		} catch (error: any) {
			console.error("Error creating product:", error);
			// Surface the backend's actual reason (e.g. "A size chart is required
			// for this category…") and STAY on the form so the merchant can fix it.
			const message =
				error?.response?.data?.message ||
				error?.response?.data?.errors?.[0] ||
				"Failed to create product. Please try again.";
			setErrors([message]);
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<FaiBox className={"product-form"}>
				<FaiBox className={"stepper-component"}>
					<Stepper activeStep={activeStep} alternativeLabel>
						{steps.map((label) => (
							<Step key={label}>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
				</FaiBox>

				{errors.length > 0 && (
					<FaiBox
						mb={2}
						p={2}
						sx={{
							backgroundColor: "rgba(255, 0, 0, 0.1)",
							borderRadius: "4px",
						}}
					>
						{errors.map((error, index) => (
							<FaiBox key={index} color="error.main">
								{error}
							</FaiBox>
						))}
					</FaiBox>
				)}

				{successMessage && (
					<FaiBox
						mb={2}
						p={2}
						sx={{
							backgroundColor: "rgba(0, 255, 0, 0.1)",
							borderRadius: "4px",
						}}
					>
						<FaiBox color="success.main">{successMessage}</FaiBox>
					</FaiBox>
				)}

				<Card>
					<FaiBox p={2}>
						<FaiBox>
							{activeStep === 0 ? (
								<FaiBox>
									<DetailForm formData={formData} setFormData={setFormData} />
								</FaiBox>
							) : (
								<FaiBox>
									<VariantForm
										formData={formData}
										setFormData={setFormData}
										variantId={createdVariantId}
									/>
								</FaiBox>
							)}
							<Grid container spacing={2}>
								{isLastStep ? (
									<Grid item sm={12} lg={5}>
										<FaiButton
											variant="contained"
											color="primary"
											fullWidth
											onClick={() => handleSaveProduct(true)}
											disabled={loading}
										>
											{loading ? (
												<CircularProgress size={24} color="inherit" />
											) : (
												"Save as Draft"
											)}
										</FaiButton>
									</Grid>
								) : (
									<Grid item sm={12} lg={5}>
										<FaiButton
											variant="contained"
											color="primary"
											fullWidth
											onClick={isLastStep ? () => { } : handleNext}
											disabled={loading}
										>
											{"Next"}
										</FaiButton>
									</Grid>
								)}
								{activeStep === 0 ? (
									<Grid item sm={12} lg={2}></Grid>
								) : (
									<Grid item sm={12} lg={2}>
										<FaiButton
											color="text"
											variant={"outlined"}
											fullWidth
											onClick={handleBack}
											disabled={loading}
										>
											Back
										</FaiButton>
									</Grid>
								)}
							</Grid>
						</FaiBox>
					</FaiBox>
				</Card>
			</FaiBox>
		</>
	);
}

export default ProductForm;
